import { Autocomplete, Divider, Grid, TextField, Tooltip, Typography, useMediaQuery } from '@mui/material';
import React, { useContext, useEffect } from 'react';
import globalStyles from "../../../../../../assets/styles/global.module.scss";
import style from "../../../../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss";
import workflowStyles from "../../../../../../assets/styles/componentStyles/manychatStyles/workflowFormComponents/workflowCreate.module.scss"
import GlobalButton from '../../../../../utility/globalButton/button';
import { functionParameterData, functionResponseData } from '../../../../../../models/axiosModel';
import Required from '../../../../../utility/required';
import { createWebhookPayload, functionResponse } from './customAPIConfigPopup';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GlobalTooltip from '../../../../../utility/tooltip/globalTooltip';
import { WorkflowContext, WorkflowContextPayload } from '../../../../../../context/workflowContext';
import { isCurrentViewVersionPublished } from '../../../utilityFunctions';

interface EditResponseVarPathPopupProps {
    selectedResPayload: functionResponse,
    setSelectedResPayload: React.Dispatch<React.SetStateAction<functionResponse | null>>,
    setFlagEditResVarPathPopup: React.Dispatch<React.SetStateAction<boolean>>,
    setPayload: React.Dispatch<React.SetStateAction<createWebhookPayload>>,
}

const EditResponseVarPathPopup: React.FC<EditResponseVarPathPopupProps> = (props) => {

    const { selectedResPayload, setSelectedResPayload, setFlagEditResVarPathPopup, setPayload } = props;
    const { currentActiveStaticWorkflow } = useContext(WorkflowContext) as WorkflowContextPayload;

    const getPathSegments = (pathStr: string) => {
        const regex = /(\[[^\]]+\])/g;
        let result: (string | { indexKey: string; original: string })[] = [];
        let lastIndex = 0;
        let indexCounter = 0;

        let match;
        while ((match = regex.exec(pathStr)) !== null) {
            const fullMatch = match[0];
            const inside = fullMatch.slice(1, -1).trim();

            const staticBefore = pathStr.slice(lastIndex, match.index);
            if (staticBefore) result.push(staticBefore);

            const unquoted = inside.replace(/^['"]|['"]$/g, '');

            if (/^\d+$/.test(unquoted) || unquoted === "INDEX") {
                result.push({ indexKey: `index_${indexCounter++}`, original: fullMatch });
            } else {
                result.push(fullMatch);
            }

            lastIndex = match.index + fullMatch.length;
        }

        const remaining = pathStr.slice(lastIndex);
        if (remaining) result.push(remaining);

        return result;
    };

    const reconstructPath = (originalPath: string, resVar: any): string => {
        const segments = getPathSegments(originalPath);

        return segments.map(segment => {
            if (typeof segment === "string") return segment;

            const val = resVar[segment.indexKey] ?? segment.original.slice(1, -1).replace(/^['"]|['"]$/g, '');

            if (val === "INDEX") {
                return `[INDEX]`;
            } else {
                return `["${val}"]`;
            }
        }).join('');
    };

    const handleUpdate = () => {
        const originalPath = selectedResPayload.action_custom_api_call_response_variable_path || "";
        const updatedPath = reconstructPath(originalPath, selectedResPayload);

        setPayload((prevState) => ({
            ...prevState,
            function_response: prevState?.function_response?.map((res) => {
                if (res.function_response_data_id == selectedResPayload.function_response_data_id) {
                    // res.action_custom_api_call_response_variable_path = updatedPath; 
                    return {
                        ...res,
                        action_custom_api_call_response_variable_path: updatedPath,
                    }
                }
                return res;
            })
        }))

        setFlagEditResVarPathPopup(false);
    }

    useEffect(() => {
        if (!selectedResPayload) return;

        const segments = getPathSegments(selectedResPayload.action_custom_api_call_response_variable_path || "");
        const newSelected: any = { ...selectedResPayload };

        segments.forEach(segment => {
            if (typeof segment === "object") {
                const val = segment.original.slice(1, -1).replace(/^['"]|['"]$/g, '');
                newSelected[segment.indexKey] = val;
            }
        });

        setSelectedResPayload(newSelected);
    }, []);

    return (
        <>
            <Grid container className={`${globalStyles.flexColumn} ${globalStyles.justifyCenter}`}>
                <Grid item className={`${globalStyles.textCenter}`}>
                    <Typography className={style.popupTitleTypography}> {`Edit Path for  ${selectedResPayload?.function_response_name}`}</Typography>
                </Grid>

                <Divider className={style.popupTitleDivider} />

                <Grid item>
                    <Grid container className={style.popupBodyGridContainer} gap={1}>
                        <Grid item xs={12}>
                            <Grid container gap={"12px"}>
                                <Grid item xs={12}>
                                    <Grid container gap={1}>
                                        <Grid item>
                                            <Typography className={style.popupFieldTypography}>Path <Required />
                                            </Typography>
                                        </Grid>
                                        <Grid item className={globalStyles.clickable}>
                                            <GlobalTooltip
                                                title="If there is an array in the path, you can choose which item (index) to use. If you don't select any index, it will show values from all items in that array, separated by commas."
                                                placement='bottom'
                                                arrow={true}
                                                children={<InfoOutlinedIcon className={workflowStyles.iButton} />}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} className={workflowStyles.responseVariablePathGrid}>
                                    {getPathSegments(selectedResPayload.action_custom_api_call_response_variable_path || "").map((segment, i) => {
                                        if (typeof segment === "string") {
                                            return (
                                                <Typography className={workflowStyles.stringSegmentTypography} component="span" key={`text-${i}`}>
                                                    {segment}
                                                </Typography>
                                            );
                                        } else {
                                            const selectedValue = (selectedResPayload as any)[segment.indexKey] ?? segment.original.slice(1, -1).replace(/^['"]|['"]$/g, '');

                                            return (
                                                <React.Fragment key={`autocomplete-${segment.indexKey}`}>
                                                    <Typography className={workflowStyles.autocompleteBracket} component="span">[</Typography>
                                                    <Autocomplete
                                                        disableClearable
                                                        options={["INDEX", ...Array.from({ length: 101 }, (_, i) => i.toString())]}
                                                        value={selectedValue}
                                                        onChange={(_, newVal) => {
                                                            setSelectedResPayload(prev =>
                                                                prev ? { ...prev, [segment.indexKey]: newVal || "INDEX" } : prev
                                                            );
                                                        }}
                                                        renderInput={(params) => (<TextField {...params} variant="outlined" size="small" />)}
                                                        className={`${style.contactFilterDropdownsInPopup} ${workflowStyles.editResVarIndexAutoComplete}`} 
                                                        disabled={isCurrentViewVersionPublished(currentActiveStaticWorkflow)}
                                                    />
                                                    <Typography className={workflowStyles.autocompleteBracket} component="span">]</Typography>
                                                </React.Fragment>
                                            );
                                        }
                                    })}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item>
                    <Grid container className={`${globalStyles.justifyCenter} ${globalStyles.alignItemCenter} ${workflowStyles.editResponseVarPathPopupBtnContainer}`} gap={"12px"} sx={{ padding: "20px 0" }}>
                        <Grid item>
                            <GlobalButton
                                buttonText="Cancel"
                                sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                className='borderedButtonStyle' 
                                disabled={isCurrentViewVersionPublished(currentActiveStaticWorkflow)}
                                onClick={() => {
                                    setFlagEditResVarPathPopup(false);
                                    setSelectedResPayload(null);
                                }}
                            />
                        </Grid>

                        <Grid item>
                            <GlobalButton
                                buttonText={"Update"}
                                sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                className='primaryButtonStyle' 
                                disabled={isCurrentViewVersionPublished(currentActiveStaticWorkflow)}
                                onClick={() => {
                                    handleUpdate();
                                }}
                            >
                            </GlobalButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

export default EditResponseVarPathPopup
